import { ChangeAnalysisScopeModalContent } from '@modules/analysis/components/ChangeAnalysisScopeModalContent';
import { useUpdateAnalysis } from '@modules/analysis/hooks/useUpdateAnalysis';
import { useAnalysisStore } from '@modules/analysis/store/analysisStore';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { ArtefactType } from '@modules/common/types';
import { useGetQuantifyQueryMetadata } from '@modules/quantify/hooks';
import { useEffect, useCallback } from 'react';
import { Scope } from 'v2/core/entities/scope';
import { mapFromEdgeScope } from 'v2/core/repository/edge-server/analysis/fromEdgeAnalysis';
import { mapToScope } from 'v2/core/repository/edge-server/analysis/toEdgeAnalysisMappers';
import useFullscreenLoader from 'v2/core/services/loader/useFullscreenLoader';
import { useGetUser } from 'v2/core/services/user';
import { shallow } from 'zustand/shallow';

export const useChangeQuantifyScope = () => {
  const { id, title, editToken, createdBy, analysisScope, onScopeChange } =
    useAnalysisStore(
      (analysis) => ({
        id: analysis.id,
        title: analysis.title,
        editToken: analysis.editToken,
        createdBy: analysis.createdBy,
        useCase: analysis.useCase,
        href: analysis.href,
        analysisScope: mapFromEdgeScope(analysis.scope),
        onScopeChange: analysis.onScopeChange,
      }),
      shallow,
    );

  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const queryMetadata = useGetQuantifyQueryMetadata();
  const { openModal, closeModal } = useMuiModalContext();
  const { showErrorNotification } = useNotificationContext();
  const { updateAnalysis, isLoading, error } = useUpdateAnalysis({
    name: title,
    scope: analysisScope,
    id: id,
    editToken: editToken,
    createdBy: createdBy,
    metadata: queryMetadata,
  });

  useFullscreenLoader({ isLoading });

  useEffect(() => {
    if (error) {
      showErrorNotification({
        contentComponent: `Failed to update scope of Quantify.`,
        props: { isClosable: true },
      });
    }
  }, [error, showErrorNotification]);

  const updateAnalysisScope = useCallback(
    async (scope: Scope) => {
      try {
        if (!editToken) {
          return;
        }

        await updateAnalysis({
          variables: {
            title,
            scope: mapToScope(scope),
            id,
            editToken,
            createdBy: createdBy?.id,
            tenantID,
            metadata: queryMetadata,
          },
        });
        onScopeChange(mapToScope(scope));
        closeModal();
      } catch {
        showErrorNotification({
          contentComponent: `Failed to update discoverability of Quantify.`,
          props: { isClosable: true },
        });
      }
    },
    [
      closeModal,
      createdBy?.id,
      editToken,
      id,
      onScopeChange,
      queryMetadata,
      showErrorNotification,
      tenantID,
      title,
      updateAnalysis,
    ],
  );

  const openChangeAnalysisScopeModal = useCallback(
    () =>
      openModal({
        contentComponent: () => (
          <ChangeAnalysisScopeModalContent
            artefactType={ArtefactType.Quantify}
            analysisScope={analysisScope}
            onScopeSave={updateAnalysisScope}
            onClose={closeModal}
          />
        ),
        modalProps: {
          fullWidth: true,
          maxWidth: 'sm',
          PaperProps: {
            elevation: 3,
          },
        },
      }),
    [analysisScope, closeModal, openModal, updateAnalysisScope],
  );

  return openChangeAnalysisScopeModal;
};
